import { useState, memo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import {
  ThemedBoltLightningIcon,
  ThemedMaximizeIcon,
  ThemedMinimizeIcon,
  ThemedCubeIcon,
  ThemedToggleOnIcon,
  ThemedHighlightOffIcon,
  ThemedConnectorIcon
} from "assets/CXFIcons/Designer/Toolbox";
import { SettingsGearIcon } from "assets/CXFIcons/Portal/SettingsMenu";
import { ThemedHelpIcon } from "assets/CXFIcons/Portal/Navigation";
import ToolboxDetails from "./components/ToolboxDetails";
import {
  StyledListItemButton,
  StyledListItemIcon,
  StyledListItemText,
  StyledDivider
} from "./components/StyledComponents";
import { TOOLBOX_MENU_ITEM_TYPES, TOOLBOX_MINIFIED_MENU_WIDTH } from "constants/flowDesigner";
import { MINI_SIDENAV_WIDTH, PORTAL_HEADER_HEIGHT } from "cxfcomponents/portalShell";
import cxfLogo from "assets/images/cxf_logo.png";
import cxfCircleLogo from "assets/images/cxfab_circle.png";
import {
  openToolboxDetails,
  closeToolboxDetails,
  setToolboxSelectedMenuItem,
  clearToolboxSelectedMenuItem
} from "redux/actions/flowDesigner/settingsActionCreator";
import { useSchedules } from "hooks/useSchedules";
import FlowSelectionDialog from "layouts/Flow Designer/components/FlowSelectionDialog/FlowListDialog";
import { openFlowListDialog } from "redux/actions/flowDesigner/flowsActionCreator";
import { LibraryAdd } from "@mui/icons-material";
import StorageOutlined from "@mui/icons-material/StorageOutlined";
import { openNewFlowDialog } from "redux/actions/portal/flowsActionCreator";
import ProtectedButton from "authentication/ProtectedButton";
import { PERMISSIONS } from "constants/portal";
import NewFlowDialog from "cxfcomponents/NewFlowDialog";

const ToolboxListItem = ({
  icon,
  title,
  allowTooltip,
  collapsed,
  selected,
  action = Function(),
  disabled = false
}) => {
  return (
    <ListItem sx={{ padding: "4px 5px" }} disabled={disabled}>
      <StyledListItemButton
        selected={selected}
        onClick={action}
        aria-label={title}
        title={allowTooltip ? title : undefined}
        sx={collapsed ? { justifyContent: "center", px: 0 } : undefined}
      >
        <StyledListItemIcon sx={collapsed ? { minWidth: 0, width: "100%", marginRight: 0 } : undefined}>
          {selected ? <ThemedHighlightOffIcon /> : icon}
        </StyledListItemIcon>
        {!collapsed && <StyledListItemText disableTypography secondary={title} />}
      </StyledListItemButton>
    </ListItem>
  );
};

const Toolbox = ({ onWidthChange }) => {
  useSchedules();
  const { showToolboxDetails, toolboxSelectedMenuItem } = useSelector(
    (store) => store?.designer?.settings
  );
  const { t } = useTranslation("flowDesigner");
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { showFlowListDialog, showNewFlowDialog } = useSelector((store) => store?.designer?.flows);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);

  const PANEL_WIDTHS = {
    COMPONENTS: 310,
    CONNECTORS: 315,
    TOOL_MANAGEMENT: 400,
    FLOW_PROPERTIES: 800,
    SESSION_VIEWER: 600,
    PREFERENCES: 320,
    TRIGGERS: 315
  };

  const getTotalWidth = () => {
    const panel = showToolboxDetails ? PANEL_WIDTHS[toolboxSelectedMenuItem] || 0 : 0;
    return panel;
  };

  useEffect(() => {
    if (onWidthChange) onWidthChange(getTotalWidth());
  }, [open, showToolboxDetails, toolboxSelectedMenuItem]);

  const handleRedirectToHome = (event) => {
    event.preventDefault();
    window.open("/home", "portal")?.focus();
  };

  const handleDrawerOpen = () => setOpen(true);

  const openDetails = () => {
    if (!showToolboxDetails) {
      dispatch(openToolboxDetails());
    }
  };

  const closeDetails = () => {
    if (showToolboxDetails) {
      dispatch(closeToolboxDetails());
      dispatch(clearToolboxSelectedMenuItem());
    }
  };
  const handleDrawerClose = () => {
    closeDetails();
    setOpen(false);
  };

  const selectMenuItem = (menuItem) => {
    if (toolboxSelectedMenuItem === menuItem) {
      closeDetails();
      return;
    }
    dispatch(setToolboxSelectedMenuItem(menuItem));
  };

  const handleComponents = () => {
    selectMenuItem(TOOLBOX_MENU_ITEM_TYPES.COMPONENTS);
    openDetails();
  };
  const handleConnectors = () => {
    selectMenuItem(TOOLBOX_MENU_ITEM_TYPES.CONNECTORS);
    openDetails();
  };
  const handlePreferences = () => {
    selectMenuItem(TOOLBOX_MENU_ITEM_TYPES.PREFERENCES);
    openDetails();
  };
  const handleFlowProperties = () => {
    if (!currentSavedFlow) return;
    selectMenuItem(TOOLBOX_MENU_ITEM_TYPES.FLOW_PROPERTIES);
    openDetails();
  };
  const handleTriggers = () => {
    selectMenuItem(TOOLBOX_MENU_ITEM_TYPES.TRIGGERS);
    openDetails();
  };
  const handleSessionViewer = () => {
    selectMenuItem(TOOLBOX_MENU_ITEM_TYPES.SESSION_VIEWER);
    openDetails();
  };
  const handleHelp = () => {
    window.open(`https://cxfabric.io/docs/#/introduction/get-started/designer`, "_blank");
  };

  const handleFlowMenuOpen = (event) => {
    if (showToolboxDetails) {
      closeDetails();
    }
    setMenuAnchorEl(event.currentTarget);
  };
  const handleFlowMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const openExistingFlow = () => {
    handleFlowMenuClose();
    dispatch(openFlowListDialog());
  };

  const createNewFlow = () => {
    handleFlowMenuClose();
    dispatch(openNewFlowDialog());
  };

  return (
    <>
      <Box
        className="toolbox-wrapper"
        sx={{
          width: open ? TOOLBOX_MINIFIED_MENU_WIDTH : MINI_SIDENAV_WIDTH,
          height: "100%",
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxSizing: "border-box",
          backgroundColor: "var(--designer-shell-bg)",
          borderRight: "1px solid var(--designer-shell-border)",
          position: "relative",
          zIndex: 11,
          transition: "width 0.3s ease"
        }}
      >
        <Box sx={{ height: PORTAL_HEADER_HEIGHT, minHeight: PORTAL_HEADER_HEIGHT, display: "flex", alignItems: "center", px: open ? 2 : 1.5, boxSizing: "border-box" }}>
          <Box component="a" href="/home" target="_blank" rel="noopener" onClick={handleRedirectToHome} title={t("tooltips.back_to_home")} sx={{ display: "flex", alignItems: "center" }}>
            <Box component="img" src={open ? cxfLogo : cxfCircleLogo} alt="CXFabric" sx={{ width: open ? 149 : 44, maxWidth: "100%", height: "auto" }} />
          </Box>
        </Box>
        <Box sx={{ px: 1, pt: 1, flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", boxSizing: "border-box" }}>
          <List>
            <ToolboxListItem
              collapsed={!open}
              icon={open ? <ThemedMinimizeIcon /> : <ThemedMaximizeIcon />}
              title={t("toolbox.menu.minimize")}
              allowTooltip={!open}
              action={open ? handleDrawerClose : handleDrawerOpen}
            />
          </List>
          <StyledDivider />
          <List>
            <ToolboxListItem
              collapsed={!open}
              title={t("toolbox.menu.flow_list")}
              icon={<LibraryAdd />}
              allowTooltip={!open}
              action={handleFlowMenuOpen}
            />
          </List>

          <Menu
            id="flow-menu"
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleFlowMenuClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            sx={{ "& .MuiPaper-root": { borderRadius: "8px", minWidth: 180 } }}
          >
            <MenuItem onClick={openExistingFlow}>{t("menus.open_existing")}</MenuItem>
            <ProtectedButton permissionName={PERMISSIONS.CREATE_WORKFLOWS}>
              <MenuItem onClick={createNewFlow}>{t("menus.create_new")}</MenuItem>
            </ProtectedButton>
          </Menu>

          <StyledDivider />
          <List>
            <ToolboxListItem
              collapsed={!open}
              selected={toolboxSelectedMenuItem === TOOLBOX_MENU_ITEM_TYPES.TRIGGERS}
              icon={<ThemedBoltLightningIcon />}
              title={t("toolbox.menu.triggers")}
              allowTooltip={!open}
              action={handleTriggers}
            />
          </List>
          <StyledDivider />
          <List>
            <ToolboxListItem
              collapsed={!open}
              selected={toolboxSelectedMenuItem === TOOLBOX_MENU_ITEM_TYPES.COMPONENTS}
              icon={<ThemedCubeIcon />}
              title={t("toolbox.menu.components")}
              allowTooltip={!open}
              action={handleComponents}
            />
            <ToolboxListItem
              collapsed={!open}
              selected={toolboxSelectedMenuItem === TOOLBOX_MENU_ITEM_TYPES.CONNECTORS}
              icon={<ThemedConnectorIcon />}
              title={t("toolbox.menu.connectors")}
              allowTooltip={!open}
              action={handleConnectors}
            />
          </List>
          <StyledDivider />
          <List>
            <ToolboxListItem
              collapsed={!open}
              selected={toolboxSelectedMenuItem === TOOLBOX_MENU_ITEM_TYPES.FLOW_PROPERTIES}
              icon={<SettingsGearIcon />}
              title={t("toolbox.menu.flow_properties")}
              allowTooltip={!open}
              action={handleFlowProperties}
              disabled={!currentSavedFlow}
            />
            <ToolboxListItem
              collapsed={!open}
              selected={toolboxSelectedMenuItem === TOOLBOX_MENU_ITEM_TYPES.PREFERENCES}
              icon={<ThemedToggleOnIcon />}
              title={t("toolbox.menu.preferences")}
              allowTooltip={!open}
              action={handlePreferences}
            />
            <StyledDivider />
            <ToolboxListItem
              collapsed={!open}
              selected={toolboxSelectedMenuItem === TOOLBOX_MENU_ITEM_TYPES.SESSION_VIEWER}
              icon={<StorageOutlined />}
              title="Session Viewer"
              allowTooltip={!open}
              action={handleSessionViewer}
            />
            <ToolboxListItem
              collapsed={!open}
              icon={<ThemedHelpIcon />}
              title={t("toolbox.menu.help")}
              allowTooltip={!open}
              action={handleHelp}
            />
          </List>
        </Box>
      </Box>
      {createPortal(
        <>
          {showToolboxDetails && <ToolboxDetails isExpanded={open} />}
          {showFlowListDialog && <FlowSelectionDialog />}
          {showNewFlowDialog && <NewFlowDialog />}
        </>,
        document.body
      )}
    </>
  );
};

export default memo(Toolbox);
